import { useCallback, useEffect, useRef } from "react";
import API_BASE, { protectedApiFetch } from "../api";
import {
  clearAnalyticsSessionId,
  getAnalyticsSessionId,
  peekAnalyticsSessionId,
} from "./trackEvent";

const ANALYTICS_HEARTBEAT_INTERVAL_MS = 60000;
const ANALYTICS_INTERACTION_HEARTBEAT_MIN_MS = 15000;

function currentPagePath() {
  if (typeof window === "undefined") {
    return "";
  }

  return `${window.location.pathname}${window.location.search}`;
}

function isRunningStandalone() {
  if (typeof window === "undefined") {
    return false;
  }

  const displayModeStandalone =
    typeof window.matchMedia === "function" &&
    window.matchMedia("(display-mode: standalone)").matches;
  const iosStandalone =
    typeof navigator !== "undefined" && navigator.standalone === true;
  return displayModeStandalone || iosStandalone;
}

async function sendAnalyticsSessionRequest(endpoint, payload, { keepalive = true } = {}) {
  try {
    const response = await protectedApiFetch(
      `${API_BASE}/analytics/sessions/${endpoint}`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
        keepalive,
      }
    );
    return response.ok;
  } catch {
    return false;
  }
}

export function startAnalyticsSession({ entryPath = "" } = {}) {
  const sessionId = getAnalyticsSessionId();
  if (!sessionId) {
    return Promise.resolve(false);
  }

  return sendAnalyticsSessionRequest("start", {
    session_id: sessionId,
    entry_path: entryPath || currentPagePath() || undefined,
    is_standalone: isRunningStandalone(),
  });
}

export function heartbeatAnalyticsSession({ keepalive = true } = {}) {
  const sessionId = peekAnalyticsSessionId();
  if (!sessionId) {
    return Promise.resolve(false);
  }

  return sendAnalyticsSessionRequest(
    "heartbeat",
    {
      session_id: sessionId,
    },
    { keepalive }
  );
}

export function endAnalyticsSession({ exitPath = "" } = {}) {
  const sessionId = peekAnalyticsSessionId();
  if (!sessionId) {
    return Promise.resolve(false);
  }

  return sendAnalyticsSessionRequest("end", {
    session_id: sessionId,
    exit_path: exitPath || currentPagePath() || undefined,
  });
}

export function clearAnalyticsSession() {
  clearAnalyticsSessionId();
}

export function useAnalyticsSession({ userId } = {}) {
  const startedRef = useRef(false);
  const lastHeartbeatAtRef = useRef(0);

  const startSession = useCallback(() => {
    if (!userId || startedRef.current) {
      return;
    }

    startedRef.current = true;
    startAnalyticsSession({ entryPath: currentPagePath() });
  }, [userId]);

  const sendHeartbeat = useCallback(
    ({ force = false, keepalive = true } = {}) => {
      if (!userId || !startedRef.current) {
        return;
      }

      const now = Date.now();
      if (
        !force &&
        now - lastHeartbeatAtRef.current < ANALYTICS_INTERACTION_HEARTBEAT_MIN_MS
      ) {
        return;
      }

      lastHeartbeatAtRef.current = now;
      heartbeatAnalyticsSession({ keepalive });
    },
    [userId]
  );

  useEffect(() => {
    if (!userId) {
      startedRef.current = false;
      lastHeartbeatAtRef.current = 0;
      return undefined;
    }

    startSession();

    return () => {
      if (startedRef.current) {
        endAnalyticsSession({ exitPath: currentPagePath() });
      }
      startedRef.current = false;
      lastHeartbeatAtRef.current = 0;
    };
  }, [startSession, userId]);

  useEffect(() => {
    if (!userId || typeof window === "undefined") {
      return undefined;
    }

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        startSession();
        sendHeartbeat({ keepalive: false });
        return;
      }

      endAnalyticsSession({ exitPath: currentPagePath() });
      startedRef.current = false;
    };
    const handleFocus = () => {
      startSession();
      sendHeartbeat({ keepalive: false });
    };
    const handlePageHide = () => {
      endAnalyticsSession({ exitPath: currentPagePath() });
      startedRef.current = false;
    };

    const intervalId = window.setInterval(() => {
      if (document.visibilityState === "visible") {
        sendHeartbeat({ force: true });
      }
    }, ANALYTICS_HEARTBEAT_INTERVAL_MS);

    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("focus", handleFocus);
    window.addEventListener("pagehide", handlePageHide);

    return () => {
      window.clearInterval(intervalId);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("focus", handleFocus);
      window.removeEventListener("pagehide", handlePageHide);
    };
  }, [sendHeartbeat, startSession, userId]);
}
